/**
 * feat-362 — dummy data for the new-hire tracker (`npm run start:fake`).
 *
 * Shapes follow CONTRACT.md r3; the people follow `design/_shared.js` fixtures.
 * Every row is a state the page must render: auto-created, needs_review
 * (all four reasons), would-create, create_error, mapped-never-recorded,
 * amber, neutral, green, auto-created with a deleted server (no badge), and
 * two dismissed rows. r3 adds every "Bespoke activity" variant: training none /
 * started-none-finished / 1 lesson / 2+ lessons / n>0 with no completion date;
 * onboarding none / no-consent / full; and a server at a different store
 * (nh-090). Also the fixture the component tests render against.
 *
 * The transition helpers mirror the contract's rules so the fake mode behaves,
 * but the indicator after a transition is a fixed value from the contract
 * (`neutral` right after a follow-up, `grey` after dismiss), not a computation.
 */

import type {
  NewHireList,
  NewHireOnboarding,
  NewHireRow,
  NewHireStatus,
  NewHireTraining,
} from './newHiresAdminService';

export const FAKE_TENANT_ID = 'tenant-ll';
const TZ = 'America/Los_Angeles';
const loc = (id: string, name: string) => ({ id, name, timezone: TZ });
const STORE_NAMES: Record<string, string> = {
  'loc-1cal': '1 California',
  'loc-cc': 'City Center',
  'loc-ucsf': 'UCSF',
  'loc-haas': 'Haas',
  'loc-580': '580 California',
  'loc-221': '221 Montgomery',
};
const srv = (id: string, name: string, location_id: string, has_phone = false) => ({
  id,
  name,
  tenant_id: FAKE_TENANT_ID,
  location_id,
  has_phone,
  location_name: STORE_NAMES[location_id] ?? '',
});

const NO_TRAINING: NewHireTraining = {
  lessons_completed: 0,
  last_completed_at: null,
  started: false,
  last_activity_at: null,
};
const training = (
  lessons_completed: number,
  last_completed_at: string | null,
  last_activity_at: string | null = last_completed_at
): NewHireTraining => ({ lessons_completed, last_completed_at, started: true, last_activity_at });
const onboarding = (onboarding_complete: boolean, consent_at: string | null = null): NewHireOnboarding => ({
  onboarding_complete,
  consent_given: consent_at !== null,
  consent_given_at: consent_at,
});
const NOT_ONBOARDED = onboarding(false);

/** `bespoke` block: training/onboarding are null iff there is no server (r3). */
const activity = (
  first_shift_at: string | null,
  last_shift_at: string | null,
  t: NewHireTraining | null,
  o: NewHireOnboarding | null
): NewHireRow['bespoke'] => ({ first_shift_at, last_shift_at, training: t, onboarding: o });

function base(overrides: Partial<NewHireRow> & Pick<NewHireRow, 'id'>): NewHireRow {
  return {
    tenant_id: FAKE_TENANT_ID,
    square: {
      display_name: 'Someone',
      team_member_id: 'TM000',
      first_clock_in_at: '2026-11-03T15:04:00Z',
      last_clock_in_at: '2026-11-04T14:58:00Z',
    },
    location: loc('loc-1cal', '1 California'),
    reason: 'unmapped',
    server: null,
    auto_created: false,
    would_create: false,
    create_error: null,
    needs_review: false,
    review_reason: null,
    candidates: [],
    bespoke: activity(null, null, null, null),
    status: 'needs_followup',
    indicator: 'red',
    recorded_at: null,
    followed_up: null,
    dismissed: null,
    first_detected_at: '2026-11-04T23:00:00Z',
    ...overrides,
  };
}

/** A row with a linked server must carry training/onboarding objects. */
function withServer(row: NewHireRow, t: NewHireTraining = NO_TRAINING, o: NewHireOnboarding = NOT_ONBOARDED): NewHireRow {
  return { ...row, bespoke: { ...row.bespoke, training: t, onboarding: o } };
}

export function fakeNewHireRows(): NewHireRow[] {
  return [
    withServer(base({
      id: 'nh-101',
      square: { display_name: 'Marisol Tapia Reyes', team_member_id: 'TMa9Qk2LzP4vX7', first_clock_in_at: '2026-11-03T15:04:00Z', last_clock_in_at: '2026-11-04T14:58:00Z' },
      server: srv('srv-501', 'Marisol Tapia Reyes', 'loc-1cal'),
      auto_created: true,
      first_detected_at: '2026-11-04T23:00:00Z',
    })),
    base({
      id: 'nh-100',
      square: { display_name: 'Jose Hernandez', team_member_id: 'TMb1Hn8RwQ0eC3', first_clock_in_at: '2026-11-02T18:31:00Z', last_clock_in_at: '2026-11-03T18:29:00Z' },
      location: loc('loc-cc', 'City Center'),
      needs_review: true,
      review_reason: 'name_match',
      candidates: [
        { server_id: 'srv-212', server_name: 'Jose Hernandez', location_id: 'loc-cc' },
        { server_id: 'srv-388', server_name: 'José Luis Hernández', location_id: 'loc-haas' },
      ],
      first_detected_at: '2026-11-03T23:00:00Z',
    }),
    base({
      id: 'nh-099',
      square: { display_name: 'Kwame Asante-Mensah', team_member_id: 'TMc7Pp3VdK9yJ1', first_clock_in_at: '2026-11-01T14:15:00Z', last_clock_in_at: '2026-11-03T15:12:00Z' },
      location: loc('loc-ucsf', 'UCSF'),
      would_create: true,
      first_detected_at: '2026-11-02T23:00:00Z',
    }),
    base({
      id: 'nh-098',
      square: { display_name: 'Riley Chen', team_member_id: 'TMk1Aa2BbC3dE4', first_clock_in_at: '2026-11-01T16:00:00Z', last_clock_in_at: '2026-11-02T16:00:00Z' },
      location: loc('loc-haas', 'Haas'),
      would_create: true,
      create_error: 'IntegrityError',
      first_detected_at: '2026-11-02T22:00:00Z',
    }),
    base({
      id: 'nh-097',
      square: { display_name: 'Ana Lopez', team_member_id: 'TMl2Bb3CcD4eF5', first_clock_in_at: '2026-11-01T16:00:00Z', last_clock_in_at: '2026-11-02T16:00:00Z' },
      location: loc('loc-580', '580 California'),
      needs_review: true,
      review_reason: 'name_taken',
      first_detected_at: '2026-11-02T21:00:00Z',
    }),
    base({
      id: 'nh-096',
      square: { display_name: null, team_member_id: 'TMm3Cc4DdE5fG6', first_clock_in_at: '2026-11-01T16:00:00Z', last_clock_in_at: '2026-11-02T16:00:00Z' },
      location: loc('loc-221', '221 Montgomery'),
      needs_review: true,
      review_reason: 'no_name',
      first_detected_at: '2026-11-02T20:00:00Z',
    }),
    base({
      id: 'nh-095',
      square: { display_name: 'Ben Okafor', team_member_id: 'TMn4Dd5EeF6gH7', first_clock_in_at: '2026-11-01T16:00:00Z', last_clock_in_at: '2026-11-02T16:00:00Z' },
      location: loc('loc-cc', 'City Center'),
      needs_review: true,
      review_reason: 'inactive_mapping',
      candidates: [{ server_id: 'srv-150', server_name: 'Ben Okafor', location_id: 'loc-cc' }],
      first_detected_at: '2026-11-02T19:00:00Z',
    }),
    base({
      id: 'nh-094',
      square: { display_name: 'Gina Russo', team_member_id: 'TMo5Ee6FfG7hI8', first_clock_in_at: '2026-10-30T16:00:00Z', last_clock_in_at: '2026-11-02T16:00:00Z' },
      location: loc('loc-ucsf', 'UCSF'),
      auto_created: true,
      server: null,
      first_detected_at: '2026-10-31T22:00:00Z',
    }),
    withServer(base({
      id: 'nh-091',
      square: { display_name: 'Nilda Ozuna Segura', team_member_id: 'TMd4Ff6GhS2tN8', first_clock_in_at: '2026-10-27T15:11:00Z', last_clock_in_at: '2026-11-03T16:14:00Z' },
      location: loc('loc-haas', 'Haas'),
      reason: 'mapped_never_recorded',
      server: srv('srv-430', 'Nilda Ozuna', 'loc-haas'),
      first_detected_at: '2026-10-28T22:00:00Z',
    }), training(0, null, '2026-11-02T19:40:00Z'), onboarding(true)),
    withServer(base({
      id: 'nh-088',
      square: { display_name: 'Dana Whitfield', team_member_id: 'TMe2Jj5KlM7uB6', first_clock_in_at: '2026-10-24T17:40:00Z', last_clock_in_at: '2026-11-03T18:44:00Z' },
      location: loc('loc-221', '221 Montgomery'),
      server: srv('srv-488', 'Dana Whitfield', 'loc-221'),
      auto_created: true,
      status: 'followed_up',
      indicator: 'amber',
      followed_up: {
        by: 'preston@bespokecast.ai',
        at: '2026-10-30T18:20:00Z',
        note: 'Talked to Rocío (GM). Dana works the lunch line Tue–Sat and the store is out of spare phones until the G9A shipment lands.',
      },
      first_detected_at: '2026-10-25T22:00:00Z',
    }), training(1, '2026-10-29T21:10:00Z'), onboarding(true)),
    withServer(base({
      id: 'nh-090',
      square: { display_name: 'Priya Raman', team_member_id: 'TMf8Ll1NoP3iD5', first_clock_in_at: '2026-10-28T14:02:00Z', last_clock_in_at: '2026-11-03T15:05:00Z' },
      location: loc('loc-580', '580 California'),
      reason: 'mapped_never_recorded',
      // r3: the linked server lives at another store than the row's assignment.
      server: srv('srv-417', 'Priya Raman', 'loc-cc'),
      status: 'followed_up',
      indicator: 'neutral',
      followed_up: { by: 'steve@bespokecast.ai', at: '2026-11-03T17:05:00Z', note: 'Mic was in the back office. Store lead will clip it on tomorrow.' },
      first_detected_at: '2026-10-29T22:00:00Z',
    }), training(2, '2026-11-01T20:05:00Z'), onboarding(true, '2026-10-30T16:00:00Z')),
    withServer(base({
      id: 'nh-084',
      square: { display_name: 'Tomás Gutiérrez', team_member_id: 'TMg3Rr9StU6oF2', first_clock_in_at: '2026-10-20T15:00:00Z', last_clock_in_at: '2026-11-03T16:02:00Z' },
      location: loc('loc-haas', 'Haas'),
      server: srv('srv-470', 'Tomás Gutiérrez', 'loc-haas', true),
      auto_created: true,
      bespoke: activity('2026-10-31T15:05:00Z', '2026-11-03T16:06:00Z', null, null),
      status: 'recording',
      indicator: 'green',
      recorded_at: '2026-10-31T22:00:00Z',
      followed_up: { by: 'preston@bespokecast.ai', at: '2026-10-27T17:00:00Z', note: 'Phone set up at the store.' },
      first_detected_at: '2026-10-21T22:00:00Z',
    }), training(6, '2026-11-03T22:30:00Z'), onboarding(true, '2026-10-31T14:50:00Z')),
    base({
      id: 'nh-086',
      square: { display_name: 'Alexandria Montgomery-Fitzgerald', team_member_id: 'TMi7Vv4WxY0qH9', first_clock_in_at: '2026-10-22T12:30:00Z', last_clock_in_at: '2026-11-02T13:34:00Z' },
      would_create: true,
      status: 'dismissed',
      indicator: 'grey',
      dismissed: { by: 'preston@bespokecast.ai', at: '2026-10-24T16:12:00Z', reason: "Doesn't need a mic" },
      first_detected_at: '2026-10-23T22:00:00Z',
    }),
    withServer(base({
      id: 'nh-072',
      square: { display_name: 'Sam Ortiz', team_member_id: 'TMj9Xx6YzA2rK1', first_clock_in_at: '2026-10-08T13:45:00Z', last_clock_in_at: '2026-11-03T14:50:00Z' },
      location: loc('loc-ucsf', 'UCSF'),
      reason: 'mapped_never_recorded',
      server: srv('srv-301', 'Sam Ortiz', 'loc-ucsf', true),
      bespoke: activity('2026-10-12T13:58:00Z', '2026-10-14T14:01:00Z', null, null),
      status: 'dismissed',
      indicator: 'grey',
      followed_up: { by: 'steve@bespokecast.ai', at: '2026-10-10T17:00:00Z', note: 'Asked the GM.' },
      dismissed: { by: 'steve@bespokecast.ai', at: '2026-10-16T20:40:00Z', reason: 'Kitchen manager, off the floor from Oct 15.' },
      first_detected_at: '2026-10-09T22:00:00Z',
    }), training(3, null, '2026-10-13T18:00:00Z'), onboarding(true, '2026-10-12T13:50:00Z')),
  ];
}

// In-memory store for start:fake so actions stick within a session.
let store: NewHireRow[] | null = null;
const rows = () => (store ??= fakeNewHireRows());
const nowIso = () => new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');

function replace(id: string, fn: (r: NewHireRow) => NewHireRow): NewHireRow {
  const list = rows();
  const i = list.findIndex((r) => r.id === id);
  if (i < 0) throw new Error('New hire not found');
  list[i] = fn(list[i]);
  return list[i];
}

export function fakeListNewHires(): Promise<NewHireList> {
  return Promise.resolve({ as_of: nowIso(), followup_amber_days: 3, rows: [...rows()] });
}

export function fakeFollowUpNewHire(id: string, note: string): Promise<NewHireRow> {
  return Promise.resolve(
    replace(id, (r) => ({
      ...r,
      status: 'followed_up',
      indicator: 'neutral',
      followed_up: { by: 'fake-admin@bespokecast.ai', at: nowIso(), note: note.trim() },
    }))
  );
}

export function fakeDismissNewHire(id: string, reason: string | null): Promise<NewHireRow> {
  return Promise.resolve(
    replace(id, (r) => ({
      ...r,
      status: 'dismissed',
      indicator: 'grey',
      dismissed: { by: 'fake-admin@bespokecast.ai', at: nowIso(), reason: reason?.trim() || "Doesn't need a mic" },
    }))
  );
}

export function fakeUndismissNewHire(id: string): Promise<NewHireRow> {
  return Promise.resolve(
    replace(id, (r) => {
      const status: NewHireStatus = r.bespoke.first_shift_at
        ? 'recording'
        : r.followed_up
          ? 'followed_up'
          : 'needs_followup';
      const indicator = status === 'recording' ? 'green' : status === 'followed_up' ? 'neutral' : 'red';
      return { ...r, status, indicator, dismissed: null };
    })
  );
}
